import * as React from "react";
import { graphql } from "gatsby";
import { GatsbyImage } from "gatsby-plugin-image";
import Layout from "../components/layout";
import Seo from "../components/seo";
import Hero from "../components/hero";
import Banner from "../components/banner";
import Booking from "../components/booking";
import Benefits from "../components/benefits";
import Slideshow from "../components/slideshow";
import Preparation from "../components/preparation";
import Questions from "../components/questions";
import ImageWithText from "../components/image-with-text";
import PrivatisationBanner from "../components/privatisation-banner";
import SlideshowProducts from "../components/slideshow-products";
import StickyBookingBar from "../components/sticky-booking-bar";
import { useIntl } from "react-intl";

const ProductTemplate = ({ data }) => {
  const {
    title,
    seo,
    destination,
    featuredImage,
    featuredImageMobile,
    headerButtonLabel,
    logo,
    intro,
    banner,
    bookingContent,
    imagesWithText,
    fullWidthImage,
    preparation,
    questions,
    quotations,
    seoSection,
    noIndex,
    bookingHeading,
    ctaLabel,
    privatiseUrl,
    privatiseText,
    displayPrivatiseBanner,
    slideshowProducts,
    duration,
    distance,
    difficulty,
    price,
    urlFillout,
  } = data.product;

  const seoTitle = seo?.title || title;
  const seoDescription = seo?.description?.description || intro?.intro;
  const seoImage = seo?.image || featuredImage;
  const canonicalURL = seo?.canonicalURL;
  const { formatMessage } = useIntl();

  const colors = {
    destination: destination.color || "var(--color-dark-yellow)",
  };

  return (
    <Layout whiteHeader={featuredImage}>
      <Seo
        title={seoTitle}
        description={seoDescription}
        image={seoImage}
        colors={colors}
        noIndex={noIndex}
        canonicalURL={canonicalURL}
      />
      <Hero
        title={destination.title}
        logo={logo}
        intro={intro?.intro}
        media={featuredImage}
        mediaMobile={featuredImageMobile}
        badge={destination.badge}
        badges={data.product.badges}
        reviews={true}
        distance={distance}
        duration={duration}
        difficulty={difficulty}
        isProductPage={true}
        imagesWithText={imagesWithText}
        destination={destination}
        referenceProduct={data.product.referenceProduit}
        stayReference={data.product.stayReference}
        modalLevels={data.translations.modalLevels}
        urlFillout={urlFillout}
        headerButtonLabel={headerButtonLabel}
      />
      <Banner
        heading={banner?.heading}
        text={banner?.text}
        link={banner?.link}
        linkText={banner?.linkText}
        active={banner?.active}
        buttonLink={banner?.buttonLink}
        product
      />
      <div id="presentation">
        <Booking
          urlFillout={urlFillout}
          product={data.product}
          reassurances={data.product.reassurances}
          bookingHeading={bookingHeading}
          ctaLabel={ctaLabel}
          privatiseUrl={privatiseUrl}
          privatiseText={privatiseText}
        >
          {bookingContent}
        </Booking>
        {fullWidthImage && (
          <GatsbyImage
            image={fullWidthImage.gatsbyImageData}
            alt={fullWidthImage.title}
          />
        )}
      </div>
      <Slideshow slides={imagesWithText} fullWidth product />
      <Slideshow
        slides={quotations}
        quotations={true}
        pictures={data.translations.ratingsImages}
      />
      <Preparation preparation={preparation} />
      {displayPrivatiseBanner && <PrivatisationBanner />}
      <div id="benefits">
        <Benefits product={data.product} />
      </div>
      <Questions questions={questions} product />
      {slideshowProducts && (
        <SlideshowProducts
          products={slideshowProducts}
          title={formatMessage({ id: "discoverOther" })}
          variant="green"
        />
      )}
      <ImageWithText
        image={seoSection?.image}
        mobileImage={seoSection?.mobileImage}
        imageAlt={seoSection?.imageAlt}
        text={seoSection?.text}
        modal={seoSection?.modal}
        link={seoSection?.link}
        className="full-width"
      />
      <StickyBookingBar
        destination={destination.title}
        duration={duration}
        price={price}
        urlFillout={urlFillout}
        ctaLabel={ctaLabel}
      />
    </Layout>
  );
};

export default ProductTemplate;

export const query = graphql`
  query ($id: String!, $locale: String!) {
    product: contentfulProduct(
      contentful_id: { eq: $id }
      node_locale: { eq: $locale }
    ) {
      id
      title
      referenceProduit
      stayReference
      urlFillout
      subtitleCarousel
      badges {
        label
        backgroundColor
        opacity
        textColor
      }
      seo {
        canonicalURL
        title
        description {
          description
        }
        image {
          description
          title
          gatsbyImageData(
            width: 1200
            height: 1200
            quality: 80
            resizingBehavior: FILL
          )
        }
      }
      noIndex
      featuredImage {
        description
        gatsbyImageData(layout: FULL_WIDTH)
        title
      }
      featuredImageMobile {
        description
        gatsbyImageData(layout: FULL_WIDTH)
        title
      }
      headerButtonLabel
      price
      logo {
        description
        title
        gatsbyImageData(
          width: 300
          height: 130
          quality: 80
          resizingBehavior: PAD
        )
      }
      intro {
        intro
      }
      banner {
        heading
        text {
          raw
        }
        link
        linkText
        active
        buttonLink {
          id
          button
          buttonBackgroundColour
          textColour
          title
          link
          target
          modal {
            ...Modal
          }
        }
      }
      bookingContent {
        raw
      }
      destination {
        title
        partner
        dateRange
        color
        products: product {
          id
          contentful_id
          destination {
            slug
          }
          order
          referenceProduit
          stayReference
          featuredImage {
            description
            gatsbyImageData(layout: FULL_WIDTH)
            title
          }
          intro {
            intro
          }
          duration
          distance
          difficulty
          price
        }
        intro {
          intro
        }
        badge {
          description
          title
          file {
            url
            details {
              image {
                width
                height
              }
            }
          }
        }
      }
      duration
      distance
      difficulty
      bookingHeading
      ctaLabel
      privatiseUrl
      privatiseText
      displayPrivatiseBanner
      fullWidthImage {
        description
        title
        gatsbyImageData(layout: FULL_WIDTH)
      }
      includedAtTheCamp
      includedOnGravel
      includedBeyondGravel
      notIncluded
      benefits_options {
        raw
      }
      reassurances {
        raw
      }
      imagesWithText {
        contentful_id
        image {
          description
          title
          gatsbyImageData(layout: FULL_WIDTH)
        }
        mobileImage {
          title
          gatsbyImageData(quality: 80)
        }
        imageAlt
        text {
          raw
        }
        link
      }
      preparation {
        raw
        references {
          contentful_id
          __typename
          image {
            description
            title
            gatsbyImageData(layout: FULL_WIDTH)
          }
          mobileImage {
            title
            gatsbyImageData(quality: 80)
          }
          imageAlt
          text {
            raw
          }
          link
        }
      }
      questions {
        contentful_id
        title
        category {
          title
        }
        content {
          raw
          references {
            ... on ContentfulAsset {
              contentful_id
              __typename
              description
              gatsbyImageData(layout: FULL_WIDTH)
            }
          }
        }
      }
      slideshowProducts {
        id
        contentful_id
        title
        subtitleCarousel
        destination {
          title
          slug
          products: product {
            contentful_id
          }
        }
        referenceProduit
        stayReference
        featuredImage {
          description
          gatsbyImageData(layout: FULL_WIDTH)
          title
        }
        duration
        badges {
          label
          backgroundColor
          opacity
          textColor
        }
      }
      quotations {
        contentful_id
        image {
          description
          title
          gatsbyImageData(layout: FULL_WIDTH)
        }
        mobileImage {
          title
          gatsbyImageData(quality: 80)
        }
        imageAlt
        text {
          raw
        }
        link
      }
      seoSection {
        image {
          description
          title
          gatsbyImageData(layout: FULL_WIDTH)
        }
        mobileImage {
          title
          gatsbyImageData(quality: 80)
        }
        imageAlt
        text {
          raw
        }
        modal {
          ...Modal
        }
        link
      }
    }

    translations: contentfulTranslations(node_locale: { eq: $locale }) {
      translations {
        key
        value
      }
      modalLevels {
        raw
      }
      ratingsImages {
        title
        description
        gatsbyImageData(
          width: 300
          height: 300
          quality: 80
          resizingBehavior: SCALE
        )
      }
    }
  }
`;
